import type {CSSProperties} from 'react';
import {radius, TOUCH_MIN} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeBatchLook = 'batch-primary' | 'batch-ghost' | 'batch-chip' | 'batch-filter' | 'batch-filter-on' | 'batch-option' | 'batch-option-on' | 'batch-chip-remove' | 'batch-row' | 'batch-row-armed';

const BATCH_LOOKS = new Set<string>(['batch-primary', 'batch-ghost', 'batch-chip', 'batch-filter', 'batch-filter-on', 'batch-option', 'batch-option-on', 'batch-chip-remove', 'batch-row', 'batch-row-armed']);
export const isWedgeBatchLook = (look: string): look is WedgeBatchLook => BATCH_LOOKS.has(look);

/* Released Batch tab controls (Wedge): the native controls of the compose pane. The action button is a 13px radius box at least 44px tall, 15px
   bold type, 12px by 18px padding and a 1px border. `batch-primary` is the accent fill with the on-accent ink and a transparent border;
   `batch-ghost` is a clear fill, the accent ink and the separator border. Held with `disabled`, either dims to 45% and takes a default
   cursor, and eases its opacity over .15s on the shared ease. The ghost chip (add meetings, the ZIP finder) is an inline pill with a
   glyph slot, 8px by 13px padding, 13px bold type, the secondary ink on the elevated fill and a card border. The filter trigger is a smaller
   pill (7px by 11px, 12.5px bold type); `batch-filter-on` is its chosen paint: the accent ink, a 12% accent fill over the elevated fill, a
   45% accent border mixed into the card border, and a soft accent glow. The option of the open menu is a full-width 9px radius row;
   `batch-option-on` is the picked row: the accent ink at weight 800 on a 14% accent veil. Each reads the exact legacy roles and keeps the
   order of the declarations of the source. The caller owns the label, the glyph, the handler and the layout (the full width of a block
   button is the caller's own style).
   The rows of the staged list (second part of the pane): `batch-chip-remove` is the 20px round close dot of a staged chip, clear, in the
   tertiary ink, no border and no padding. `batch-row` is a suggestion or a found place: a full-width left-aligned flex row with a 10px gap,
   9px by 11px padding, an 11px radius and no border; held (a row already queued) it dims to 50% and takes a default cursor.
   `batch-row-armed` adds the 1px red border of a flagged place that waits for its second tap. */
const BTN_FONT = 15, BTN_PAD = '12px 18px', BTN_DIM = 0.45;
const CHIP_FONT = 13, CHIP_PAD = '8px 13px', CHIP_GAP = 7;
const FILTER_FONT = 12.5, FILTER_PAD = '7px 11px', FILTER_GAP = 6;
const OPTION_FONT = 13, OPTION_PAD = '8px 10px', OPTION_GAP = 9;
const accentMix = (percent: number, over: string) => `color-mix(in srgb, ${wedgeExactRole('accent')} ${percent}%, ${over})`;

export function wedgeBatchStyle(look: WedgeBatchLook, disabled = false): CSSProperties {
  const r = wedgeExactRole;
  if (look === 'batch-primary' || look === 'batch-ghost') {
    const primary = look === 'batch-primary';
    return {
      font: 'inherit', fontFamily: r('font'), fontSize: BTN_FONT, fontWeight: 700,
      color: primary ? r('onAccent') : r('accent'), background: primary ? r('accent') : 'transparent',
      border: `1px solid ${primary ? 'transparent' : r('separator')}`, borderRadius: 13, padding: BTN_PAD, minHeight: TOUCH_MIN,
      cursor: disabled ? 'default' : 'pointer', opacity: disabled ? BTN_DIM : 1, transition: `opacity .15s ${r('ease')}`, boxSizing: 'border-box',
    };
  }
  if (look === 'batch-chip') {
    return {
      display: 'inline-flex', alignItems: 'center', gap: CHIP_GAP, padding: CHIP_PAD, borderRadius: radius.pill,
      font: 'inherit', fontFamily: r('font'), fontSize: CHIP_FONT, fontWeight: 700, color: r('labelSec'),
      background: r('bgElev'), border: `1px solid ${r('cardBorder')}`, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? BTN_DIM : 1,
    };
  }
  if (look === 'batch-filter' || look === 'batch-filter-on') {
    const on = look === 'batch-filter-on';
    return {
      display: 'inline-flex', alignItems: 'center', gap: FILTER_GAP, padding: FILTER_PAD, borderRadius: radius.pill,
      font: 'inherit', fontFamily: r('font'), fontSize: FILTER_FONT, fontWeight: 700, cursor: 'pointer',
      color: on ? r('accent') : r('labelSec'),
      background: on ? accentMix(12, r('bgElev')) : r('bgElev'),
      border: `1px solid ${on ? accentMix(45, r('cardBorder')) : r('cardBorder')}`,
      boxShadow: on ? `0 6px 16px -10px ${r('accent')}` : 'none',
    };
  }
  if (look === 'batch-chip-remove') {
    return {
      width: 20, height: 20, flexShrink: 0, borderRadius: radius.pill, border: 'none', cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      background: 'transparent', color: r('labelTer'), padding: 0,
    };
  }
  if (look === 'batch-row' || look === 'batch-row-armed') {
    return {
      width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10,
      padding: '9px 11px', borderRadius: 11, border: look === 'batch-row-armed' ? `1px solid ${r('red')}` : 'none',
      cursor: disabled ? 'default' : 'pointer', background: 'transparent', font: 'inherit',
      fontFamily: r('font'), opacity: disabled ? 0.5 : 1,
    };
  }
  const on = look === 'batch-option-on';
  return {
    width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: OPTION_GAP, padding: OPTION_PAD, borderRadius: 9,
    border: 'none', cursor: 'pointer', background: on ? accentMix(14, 'transparent') : 'transparent',
    font: 'inherit', fontFamily: r('font'), fontSize: OPTION_FONT, fontWeight: on ? 800 : 600, color: on ? r('accent') : r('label'),
  };
}

/* The Batch field: a one-line text input, 15px type in the label ink on the elevated fill, a card border, a 12px radius, 11px by 13px
   padding and a 44px minimum height, no outline. It keeps the room of the soft keyboard: a focused field scrolls clear of the inset the
   shell publishes (0px elsewhere). The grow factor is the caller's own style. */
export function wedgeBatchFieldStyle(): CSSProperties {
  const r = wedgeExactRole;
  return {
    minWidth: 0, font: 'inherit', fontFamily: r('font'), fontSize: BTN_FONT, color: r('label'), background: r('bgElev'),
    border: `1px solid ${r('cardBorder')}`, borderRadius: radius.lg, padding: '11px 13px', minHeight: TOUCH_MIN, outline: 'none',
    boxSizing: 'border-box', scrollMarginBottom: 'calc(var(--rv-kb-h, 0px) + 12px)',
  };
}
